/* «Говорящий кружок» (APP-SPEC-4): один экран на «Говорящий кружок», «Фото говорит», «Видеооткрытку», «Видео говорит»,
   «Кружок из видео», «Кружок из аудио», «Видео другим голосом» и шаблоны формата «кружок». Шапка с названием по входу
   и формату (titleOf), тело формы (CircleBody), белая «Создать кружок · N C» над меню (ToolCreateButton), хвост заказа
   (CircleOrderResult на общем OrderTail). Цена — только от сервера (expected_price формы, единица — app.price). */
import type { UserAvatar } from '../../../api/people';
import { uiText } from '../../../i18n';
import { artworkUrl } from '../studio-art-model';
import { studioCheapOffer, studioExpectedPrice, type StudioApp } from '../studio-api';
import { useStudioForm } from '../use-studio-form';
import { useStudioOrderSourcePreview } from '../use-studio-photo-previews';
import '../native-template.css';
import { CircleBody } from './CircleInputSections';
import { CirclePicture } from './CircleHero';
import { CircleOrderResult } from './CircleOrderResult';
import { CircleViz } from './circle-viz';
import { ToolCreateButton } from './ToolCreateButton';
import { ToolHeader } from './ToolHeader';
import { leaveTail, useTailRetry } from './tool-helpers';
import { circleEntry } from './circle-entry';
import { useCircleScreen } from './use-circle-screen';
import './circle.css';

export function CircleOrderForm({
  app,
  avatar,
  initialInputs,
  templateEntry = false,
}: {
  app: StudioApp;
  avatar: UserAvatar | null;
  initialInputs?: Record<string, unknown>;
  /** Открыт через «Попробовать» на листе шаблона: только такой вход закрепляет формат шаблона (circle-entry) */
  templateEntry?: boolean;
}) {
  const state = useStudioForm({ app, avatar, initialInputs });
  const screen = useCircleScreen(state, avatar, circleEntry(app, initialInputs, templateEntry));
  const retry = useTailRetry(state);
  const { form, intent, orderId, busy, submissionBlocked, error } = state;
  const price = form ? studioExpectedPrice(form, state.inputs) : null;
  const cheap = form ? studioCheapOffer(form) : null;
  const unit = screen.unit !== null && screen.unit > 0 ? screen.unit : form?.chargeable && price ? price : null;
  const view = { ...screen, unit };
  /* Страница заказа: кадр своего фото — превью входа заказа с сервера. Локальная ссылка файла живёт, пока
     смонтирована рамка загрузки: после смены формы хвостом она отозвана (стенд: «Видео говорит» — пустая рамка,
     ERR_FILE_NOT_FOUND), а при повторном входе её нет вовсе.
     У ролика сервер превью не отдаёт (403 upload_preview_forbidden) — там остаётся кадр формы, пока он жив. */
  const orderSource = useStudioOrderSourcePreview(state, screen.keys.photo ?? 'photo', !!orderId && (screen.who === 'photo' || !screen.who));
  const picture =
    orderId && orderSource && (screen.who === 'photo' || !screen.who) ? (
      <CirclePicture url={orderSource} />
    ) : screen.who === 'audio' ? (
      <CircleViz style={screen.vizValue} />
    ) : screen.who === 'photo' || screen.who === 'video' ? (
      <CirclePicture
        url={(screen.who === 'photo' ? screen.photoMedia?.url : screen.videoMedia?.url) ?? null}
        video={screen.who === 'video'}
        crop={screen.crop}
      />
    ) : (
      <CirclePicture url={artworkUrl(screen.hero.image)} />
    );
  return (
    <section className="circle-screen native-studio-form ap-in ls tc-in">
      <ToolHeader title={screen.title} />
      {!form && !error && <p role="status">{uiText('Загружаем…')}</p>}
      {error && !orderId && (
        <p role="alert">
          {uiText(error)}{' '}
          {!intent && (
            <button type="button" onClick={() => state.setRevision(value => value + 1)}>
              {uiText('Обновить')}
            </button>
          )}
        </p>
      )}
      {form && !orderId && (
        <form
          id="apBody"
          className="circle-body"
          onSubmit={event => {
            event.preventDefault();
            if (!busy && !submissionBlocked && (intent || (!screen.missing && form.available))) void state.submit();
          }}
        >
          <CircleBody screen={view} state={state} avatar={avatar} />
          <ToolCreateButton
            label={screen.round ? 'Создать кружок' : 'Создать видео'}
            price={price}
            chargeable={form.chargeable}
            missing={screen.missing}
            busy={busy}
            uploading={state.uploadingFile}
            blocked={submissionBlocked}
            pending={!!intent}
            available={form.available}
            onCreate={() => void state.submit()}
            cheap={cheap ? { price: cheap.price, where: screen.round ? 'на кружке' : 'на видео', go: () => void state.submit({ cheap: true }) } : null}
          />
        </form>
      )}
      {!form && intent && !orderId && (
        <button type="button" className="btn" disabled={busy || submissionBlocked} onClick={() => void state.submit()}>
          {uiText('Продолжить проверку')}
        </button>
      )}
      <CircleOrderResult
        state={state}
        snapshot={{
          who: screen.who === 'audio' ? screen.hero.name : screen.hero.name !== 'Кто говорит?' ? screen.hero.name : '',
          voice: screen.voice?.rows.find(row => row.value === screen.voice?.current)?.short ?? screen.voice?.rows.find(row => row.value === screen.voice?.current)?.label ?? screen.voice?.other?.name,
          picture,
          revoice: screen.speech === 'own' ? screen.policy?.video_revoice === true : screen.speech === 'audio' && !!screen.keys.audioVoice,
          audio: screen.who === 'audio',
        }}
        onRetry={retry}
        onFormat={format =>
          void leaveTail(state).then(done => {
            if (done && screen.keys.format) state.setInputs(current => ({ ...current, [screen.keys.format!]: format }));
          })
        }
        onVoice={() =>
          void leaveTail(state).then(done => {
            if (done) requestAnimationFrame(() => document.querySelector<HTMLButtonElement>('.circle-settings [data-sc-cell="voice"]')?.click());
          })
        }
      />
    </section>
  );
}
